import React, { useState, useEffect } from "react";
import { ChevronLeft, ChevronRight } from "lucide-react";

// Import screenshots
import inventoryImg from "../assets/UI.png";
import barcodeImg from "../assets/barcode.jpeg";
import billingImg from "../assets/billing.jpeg";

const AppPreview = () => {
  const [activeTab, setActiveTab] = useState(0);
  const [isPaused, setIsPaused] = useState(false);

  const slides = [
    {
      title: "Smart Billing",
      subtitle: "Generate bills in seconds",
      image: billingImg,
    },
    {
      title: "Barcode Scanner",
      subtitle: "Scan products instantly",
      image: barcodeImg,
    },
    {
      title: "Inventory Management",
      subtitle: "Track stock levels automatically",
      image: inventoryImg,
    },
  ];

  const nextSlide = () =>
    setActiveTab((prev) => (prev + 1) % slides.length);

  const prevSlide = () =>
    setActiveTab((prev) => (prev - 1 + slides.length) % slides.length);

  // Auto-slide
  useEffect(() => {
    if (isPaused) return;
    const timer = setInterval(nextSlide, 3000);
    return () => clearInterval(timer);
  }, [activeTab, isPaused]);

  const getIndex = (offset) =>
    (activeTab + offset + slides.length) % slides.length;

  return (
    <section className="py-16 sm:py-24 bg-white overflow-hidden">
      {/* Header */}
      <div className="text-center mb-10 sm:mb-14 px-4">
        <h2 className="text-emerald-600 font-bold uppercase text-xs sm:text-sm mb-2">
          App Preview
        </h2>
        <h3 className="text-2xl sm:text-4xl font-bold text-slate-900 mb-3">
          See RetailMitra in Action
        </h3>
        <p className="text-gray-500 max-w-xl mx-auto text-sm sm:text-base">
          Modern billing & inventory tools designed for Indian retailers
        </p>
      </div>

      {/* Preview Stack */}
      <div
        className="relative flex items-center justify-center h-[420px] sm:h-[520px] lg:h-[620px]"
        onMouseEnter={() => setIsPaused(true)}
        onMouseLeave={() => setIsPaused(false)}
      >
        {/* LEFT PHONE — hidden on mobile */}
        <PhoneMockup
          image={slides[getIndex(-1)].image}
          className="hidden lg:block -translate-x-[160px] scale-90 opacity-60 blur-[1px]"
        />

        {/* CENTER PHONE */}
        <PhoneMockup
          image={slides[activeTab].image}
          className="z-10 scale-100 shadow-2xl"
        />

        {/* RIGHT PHONE — hidden on mobile */}
        <PhoneMockup
          image={slides[getIndex(1)].image}
          className="hidden lg:block translate-x-[160px] scale-90 opacity-60 blur-[1px]"
        />

        {/* Desktop Navigation */}
        <button
          onClick={prevSlide}
          className="hidden sm:flex absolute left-4 p-3 rounded-full bg-white shadow hover:scale-110 transition"
        >
          <ChevronLeft />
        </button>
        <button
          onClick={nextSlide}
          className="hidden sm:flex absolute right-4 p-3 rounded-full bg-white shadow hover:scale-110 transition"
        >
          <ChevronRight />
        </button>
      </div>

      {/* Mobile Navigation */}
      <div className="flex sm:hidden justify-center gap-6 mt-6">
        <button
          onClick={prevSlide}
          className="p-3 rounded-full bg-white shadow"
        >
          <ChevronLeft />
        </button>
        <button
          onClick={nextSlide}
          className="p-3 rounded-full bg-white shadow"
        >
          <ChevronRight />
        </button>
      </div>

      {/* Text + Dots */}
      <div className="text-center mt-8 sm:mt-10 px-4">
        <h4 className="text-lg sm:text-xl font-bold">
          {slides[activeTab].title}
        </h4>
        <p className="text-gray-500 text-sm sm:text-base">
          {slides[activeTab].subtitle}
        </p>

        <div className="flex justify-center gap-2 mt-4">
          {slides.map((_, i) => (
            <button
              key={i}
              onClick={() => setActiveTab(i)}
              className={`h-1.5 rounded-full transition-all ${
                i === activeTab
                  ? "w-8 bg-emerald-500"
                  : "w-2 bg-slate-300"
              }`}
            />
          ))}
        </div>
      </div>
    </section>
  );
};

export default AppPreview;

/* ---------------- Phone Mockup ---------------- */

const PhoneMockup = ({ image, className }) => (
  <div
    className={`absolute w-[200px] h-[420px]
      sm:w-[240px] sm:h-[500px]
      lg:w-[260px] lg:h-[540px]
      bg-black rounded-[2.5rem] sm:rounded-[3rem]
      border-[8px] sm:border-[10px] border-slate-800
      overflow-hidden transition-all duration-700 ${className}`}
  >
    {/* Notch */}
    <div className="absolute top-2 left-1/2 -translate-x-1/2 w-20 sm:w-24 h-4 bg-black rounded-full z-10"></div>

    <img
      src={image}
      alt="RetailMitra App"
      className="w-full h-full object-cover"
      loading="lazy"
    />
  </div>
);
